import { call, put, takeEvery, takeLatest } from 'redux-saga/effects'
import {
  AdminCreateCustomer,
  AdminFetchCustomer,
  AdminFetchCustomers,
  AdminLoginAsCustomer,
  AdminOnboardMasterContractor,
  AdminUpdateCustomer,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import type {
  CreateCustomerPayload,
  Customer,
  LoginAsCustomerResult,
  UpdateCustomerLogoPayload,
} from 'src/interfaces/customer'
import { captureSentryError } from 'src/utils/sentry'
import { normalizeApiListPayload } from 'src/utils/normalizeApiListPayload'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import type { AsyncAction } from 'src/utils/reduxUtils'
import { callSecureApi } from './api/callApi'

export function* onFetchCustomers(action: AsyncAction<void>) {
  try {
    // `all: 'true'` bypasses the backend's `@Max(100)` limit cap; `offset` is pinned to 0
    // because the backend still applies `skip(offset)` even when `all` is set. The client
    // owns filtering, searching, sorting and pagination from here.
    const { payload }: { payload: unknown } = yield call(
      callSecureApi,
      Endpoints.AdminFetchCustomers,
      {
        method: 'GET',
        query: { all: 'true', offset: 0 },
      },
    )

    const customers = normalizeApiListPayload<Customer>(payload)

    yield put(AdminFetchCustomers.success(customers))

    if (action.next) {
      action.next(null, customers)
    }
  } catch (err: unknown) {
    captureSentryError(err, { message: '[saga.customer.onFetchCustomers] error' })

    yield put(AdminFetchCustomers.failed(err))

    if (action.next) {
      action.next(resolveErrorMessage(err, 'Failed to fetch customers'))
    }
  }
}

interface FetchCustomerPayload {
  customerId?: string
}

export function* onFetchCustomer(action: AsyncAction<FetchCustomerPayload>) {
  const customerId = action.payload?.customerId

  if (!customerId) {
    yield put(AdminFetchCustomer.failed(new Error('Missing customerId')))

    if (action.next) {
      action.next('Missing customerId')
    }

    return
  }

  try {
    const { payload }: { payload: Customer } = yield call(
      callSecureApi,
      Endpoints.AdminFetchCustomer,
      {
        method: 'GET',
        params: [{ field: ':customerId', value: customerId }],
      },
    )

    yield put(AdminFetchCustomer.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: unknown) {
    captureSentryError(err, { message: '[saga.customer.onFetchCustomer] error' })

    yield put(AdminFetchCustomer.failed(err))

    if (action.next) {
      action.next(resolveErrorMessage(err, 'Failed to fetch customer'))
    }
  }
}

export function* onCreateCustomer(action: AsyncAction<CreateCustomerPayload>) {
  try {
    const { payload }: { payload: Customer } = yield call(
      callSecureApi,
      Endpoints.AdminCreateCustomer,
      {
        method: 'POST',
        body: action.payload,
      },
    )

    yield put(AdminCreateCustomer.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: unknown) {
    captureSentryError(err, { message: '[saga.customer.onCreateCustomer] error' })

    yield put(AdminCreateCustomer.failed(err))

    if (action.next) {
      action.next(resolveErrorMessage(err, 'Failed to create customer'))
    }
  }
}

/**
 * The response is the raw saved entity — its `logoUrl` is the storage path, not a signed URL —
 * so nothing here is written into the customer slice. Callers refetch the customer to get a
 * displayable URL.
 */
export function* onUpdateCustomerLogo(action: AsyncAction<UpdateCustomerLogoPayload>) {
  const { customerId, logo } = action.payload

  try {
    const { payload }: { payload: Customer } = yield call(
      callSecureApi,
      Endpoints.AdminUpdateCustomer,
      {
        method: 'PUT',
        params: [{ field: ':customerId', value: customerId }],
        body: { logo },
      },
    )

    yield put(AdminUpdateCustomer.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: unknown) {
    captureSentryError(err, { message: '[saga.customer.onUpdateCustomerLogo] error' })

    yield put(AdminUpdateCustomer.failed(err))

    if (action.next) {
      action.next(resolveErrorMessage(err, 'Failed to update customer logo'))
    }
  }
}

interface LoginAsCustomerPayload {
  partnerId?: string
}

export function* onLoginAsCustomer(action: AsyncAction<LoginAsCustomerPayload>) {
  const partnerId = action.payload?.partnerId

  if (!partnerId) {
    yield put(AdminLoginAsCustomer.failed(new Error('Missing partnerId')))

    if (action.next) {
      action.next('Missing partnerId')
    }

    return
  }

  try {
    const { payload }: { payload: LoginAsCustomerResult } = yield call(
      callSecureApi,
      Endpoints.AdminLoginAsCustomer,
      {
        method: 'POST',
        params: [{ field: ':customerId', value: partnerId }],
      },
    )

    yield put(AdminLoginAsCustomer.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: unknown) {
    captureSentryError(err, { message: '[saga.customer.onLoginAsCustomer] error' })

    yield put(AdminLoginAsCustomer.failed(err))

    if (action.next) {
      action.next(resolveErrorMessage(err, 'Failed to log in as customer'))
    }
  }
}

interface OnboardMasterContractorPayload {
  partnerId?: string
}

export function* onOnboardMasterContractor(action: AsyncAction<OnboardMasterContractorPayload>) {
  const partnerId = action.payload?.partnerId

  if (!partnerId) {
    yield put(AdminOnboardMasterContractor.failed(new Error('Missing partnerId')))

    if (action.next) {
      action.next('Missing partnerId')
    }

    return
  }

  try {
    const { payload }: { payload: unknown } = yield call(
      callSecureApi,
      Endpoints.AdminOnboardMasterContractor,
      {
        method: 'POST',
        body: { partnerId },
      },
    )

    yield put(AdminOnboardMasterContractor.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: unknown) {
    captureSentryError(err, { message: '[saga.customer.onOnboardMasterContractor] error' })

    yield put(AdminOnboardMasterContractor.failed(err))

    if (action.next) {
      action.next(resolveErrorMessage(err, 'Failed to complete master contractor onboarding'))
    }
  }
}

export function* customerSaga() {
  // Refresh and route changes re-trigger the fetches, so only the newest run matters.
  yield takeLatest(AdminFetchCustomers.type.REQUEST, onFetchCustomers)
  yield takeLatest(AdminFetchCustomer.type.REQUEST, onFetchCustomer)
  // Mutations must each run.
  yield takeEvery(AdminCreateCustomer.type.REQUEST, onCreateCustomer)
  yield takeEvery(AdminUpdateCustomer.type.REQUEST, onUpdateCustomerLogo)
  yield takeEvery(AdminLoginAsCustomer.type.REQUEST, onLoginAsCustomer)
  yield takeEvery(AdminOnboardMasterContractor.type.REQUEST, onOnboardMasterContractor)
}
